import { normalizeReferralCode } from "./referralRouting";

const API_BASE = process.env.REACT_APP_API_BASE || "https://api.getevo.app";
const VALID_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const INVALID_CACHE_TTL_MS = 5 * 60 * 1000;

type CachedAffiliateCode = {
  expiresAt: number;
  valid: boolean;
};

function cacheKey(code: string) {
  return `evo_affiliate_code_valid:${code}`;
}

export function getCachedAffiliateCodeValidity(code: string) {
  const normalized = normalizeReferralCode(code);
  if (!normalized || typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(cacheKey(normalized));
    if (!raw) return null;
    const cached = JSON.parse(raw) as CachedAffiliateCode;
    if (!cached || cached.expiresAt <= Date.now()) {
      window.localStorage.removeItem(cacheKey(normalized));
      return null;
    }
    return cached.valid;
  } catch {
    return null;
  }
}

function setCachedAffiliateCodeValidity(code: string, valid: boolean) {
  const normalized = normalizeReferralCode(code);
  if (!normalized || typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      cacheKey(normalized),
      JSON.stringify({
        expiresAt: Date.now() + (valid ? VALID_CACHE_TTL_MS : INVALID_CACHE_TTL_MS),
        valid,
      }),
    );
  } catch {}
}

export async function validateAffiliateCode(code: string) {
  const normalized = normalizeReferralCode(code);
  if (!normalized) return false;

  const cached = getCachedAffiliateCodeValidity(normalized);
  if (cached !== null) return cached;

  const response = await fetch(
    `${API_BASE}/api/v1/public/affiliate-codes/${encodeURIComponent(normalized)}`,
    {
      headers: { Accept: "application/json" },
    },
  );

  if (response.ok) {
    const body = (await response.json()) as { valid?: boolean };
    const valid = body.valid === true;
    setCachedAffiliateCodeValidity(normalized, valid);
    return valid;
  }

  if (response.status === 400 || response.status === 404) {
    setCachedAffiliateCodeValidity(normalized, false);
    return false;
  }

  throw new Error(`affiliate code validation failed: ${response.status}`);
}
